// features/playbook-feature.js — PLAYBOOK FEATURE WRAPPER
// ═══════════════════════════════════════════════════════════
// Thin state-owning wrapper around the v1 Playbook UI
// (features/playbook.js) — which Tim prefers and we're bringing
// back verbatim. This file manages the state the v1 component
// expects as props, keeping the shell lean.
//
// Clicking Gameplan on an item opens a single-tab synthesis thread
// (ChatArtifactFeature, no Lens/Depth) — same pattern used for
// Notes + Artifacts multi-select synthesis.
// ═══════════════════════════════════════════════════════════

import { useState } from 'react';
import Playbook from './playbook';
import ChatArtifactFeature from './chat-artifact-feature';
import PlaybookCreateChat from './playbook-create-chat';
import { usePlaybookItems } from '../hooks/use-playbook-items';

export default function PlaybookFeature({ userId, supabase, userRecord, onClose }) {
  const { items, loading, deleteItem, createItem, refresh } = usePlaybookItems({ userId, supabase });

  // Manual-create overlay state — lifted out of the shell. v1
  // Playbook expects these as props; we hold them here.
  const [showPlaybookCreate, setShowPlaybookCreate] = useState(false);
  const [pbCreateType, setPbCreateType] = useState('process');
  const [pbCreateTitle, setPbCreateTitle] = useState('');
  const [pbCreateContent, setPbCreateContent] = useState('');
  const [pbCreateTags, setPbCreateTags] = useState('');

  // Chat-based create overlay — header "+ Create with Shift" button
  const [showCreateChat, setShowCreateChat] = useState(false);

  // Synthesis thread state — when Gameplan fires on an item, open
  // a thread with it as starting context.
  const [activeSession, setActiveSession] = useState(null);

  async function handleCreatePlaybookItem() {
    if (!pbCreateTitle.trim() || !pbCreateContent.trim()) return;
    const content = buildContent(pbCreateType, pbCreateContent);
    const tags = pbCreateTags.split(',').map(t => t.trim()).filter(Boolean);
    const item = await createItem({
      itemType: pbCreateType, title: pbCreateTitle.trim(),
      content, contextTags: tags,
    });
    if (item) {
      setShowPlaybookCreate(false);
      setPbCreateTitle(''); setPbCreateContent(''); setPbCreateTags('');
    }
  }

  async function handleStartGameplan(item) {
    if (!item) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token || ''}` };
      const shaped = {
        id: item.id,
        title: item.title || 'Playbook item',
        artifact_type: 'Playbook',
        content: serializeContent(item),
        description: shortDesc(item),
      };
      const res = await fetch('/api/user/send-to-thread', {
        method: 'POST', headers,
        body: JSON.stringify({
          createNew: true,
          newTitle: shaped.title,
          description: shaped.description,
          items: [shaped],
        }),
      });
      if (res.ok) {
        const { threadId } = await res.json();
        setActiveSession({ sessionId: threadId, item });
      }
    } catch (e) { console.error('handleStartGameplan:', e); }
  }

  // Synthesis thread takes over the panel while active
  if (activeSession) {
    return (
      <div className="shift-panel-overlay" style={{ display: 'flex', flexDirection: 'column' }}>
        <ChatArtifactFeature
          userId={userId} supabase={supabase} userRecord={userRecord}
          artifact={{ title: activeSession.item?.title || 'Playbook', artifact_type: 'Playbook' }}
          sessionId={activeSession.sessionId}
          onBack={() => { setActiveSession(null); refresh(); }}
        />
      </div>
    );
  }

  return (
    <>
      <Playbook
        onClose={onClose}
        isMobile={false}
        userRecord={userRecord}
        playbookItems={items}
        playbookLoading={loading}
        deletePlaybookItem={(id) => deleteItem(id)}
        startGameplan={handleStartGameplan}
        seedPlaybook={refresh}
        createPlaybookItem={handleCreatePlaybookItem}
        showPlaybookCreate={showPlaybookCreate}
        setShowPlaybookCreate={setShowPlaybookCreate}
        pbCreateType={pbCreateType} setPbCreateType={setPbCreateType}
        pbCreateTitle={pbCreateTitle} setPbCreateTitle={setPbCreateTitle}
        pbCreateContent={pbCreateContent} setPbCreateContent={setPbCreateContent}
        pbCreateTags={pbCreateTags} setPbCreateTags={setPbCreateTags}
        onCreateWithShift={() => setShowCreateChat(true)}
      />

      {showCreateChat && (
        <PlaybookCreateChat
          userId={userId}
          supabase={supabase}
          userRecord={userRecord}
          onClose={() => setShowCreateChat(false)}
          onSaved={() => { setShowCreateChat(false); refresh(); }}
          onManual={() => {
            // Fall back to the v1 manual form
            setShowCreateChat(false);
            setPbCreateType('process');
            setShowPlaybookCreate(true);
          }}
        />
      )}
    </>
  );
}

// ── Create-form helpers ───────────────────────────────────
// Shape the content field based on item type so the backend stores
// a consistent schema per subtype (matches the v1 create flow).
function buildContent(itemType, rawContent) {
  const text = (rawContent || '').trim();
  if (itemType === 'snippet') return { text };
  if (itemType === 'prompt')  return { prompt_text: text, use_case: '', example_result: '', import_reason: '' };
  if (itemType === 'template') return { structure: text, placeholder_fields: [], portrait_prefill_map: {} };
  if (itemType === 'cheatsheet') {
    return {
      sections: [{
        heading: 'Content',
        rows: text.split('\n').filter(Boolean).map(line => {
          const [k, ...v] = line.split(':');
          return { key: (k || line).trim(), value: v.join(':').trim() || '' };
        }),
      }],
    };
  }
  return { text };
}

// Serialize any playbook item's content into a readable chat seed
function serializeContent(item) {
  const c = item.content || {};
  if (typeof c === 'string') return c;
  const parts = [];
  if (c.purpose) parts.push(c.purpose);
  if (c.prompt_text) parts.push(c.prompt_text);
  if (c.text) parts.push(c.text);
  if (c.structure) parts.push(c.structure);
  if (Array.isArray(c.sections)) {
    c.sections.forEach(s => {
      if (s.heading) parts.push(`## ${s.heading}`);
      if (Array.isArray(s.rows)) s.rows.forEach(r => parts.push(`- ${r.key}: ${r.value}`));
    });
  }
  if (Array.isArray(c.blocks)) {
    c.blocks.forEach(b => {
      if (b.heading) parts.push(`## ${b.heading}`);
      if (b.body) parts.push(b.body);
    });
  }
  return parts.filter(Boolean).join('\n\n') || JSON.stringify(c);
}

function shortDesc(item) {
  const c = item.content;
  if (!c) return item.title || '';
  if (typeof c === 'string') return c.slice(0, 200);
  return (c.purpose || c.use_case || c.description || c.summary || c.text || '').slice(0, 200);
}
